import { GetStaticProps } from 'next';
import { fetchBlogs } from '@/entities/Blogs/service';
import { fetchState } from '@/common/service/api/Destination/Destination';
import { getRedirectsPath } from '@/common/service/api/Redirects/getRedirectsPath';
import { fetchLandmarksByState } from '@/screens/LandmarkMainPage/service/fetchLandmarksByCityId';
import StatePage, { IStatePageProps } from '@/screens/StatePage/StatePage';
import URL, { buildRootUrl } from '@/common/app/utils/urlsUtils';
import { checkImage } from '@/common/app/utils/checkImage';
import { getRecommends } from '@/shared/FeaturedActivities/service/ApiFeaturedActivities';
import { getPlaceholders } from '@/common/service/api/Activity/Activity';

type paramsProps = {
    state: string;
};

export async function getStaticPaths() {
    return {
        paths: [],
        fallback: 'blocking',
    };
}

export const getStaticProps: GetStaticProps<IStatePageProps, paramsProps> = async ({ params }) => {
    try {
        if (!params) {
            throw new Error(`PAGE DESTINATION SLUG: BAD PARAMS: ${params}`);
        }
        const stateCode = params.state.toLowerCase();

        const state = await fetchState(stateCode);

        if (!state || !state.total_activities) {
            const redirectPath = await getRedirectsPath(`destination/${stateCode}/`);

            if (redirectPath === '/404/') {
                return {
                    notFound: true,
                };
            }

            return {
                revalidate: Number(process.env.CACHE_ENV),
                redirect: {
                    destination: redirectPath.toLowerCase(),
                    permanent: true,
                },
            };
        }

        const [blogs, landmarks, mainImage, recommends] = await Promise.all([
            fetchBlogs({ state: stateCode, limit: 4 }),
            fetchLandmarksByState(stateCode, true),
            checkImage(`/state/${state.id}/ts`, true),
            getRecommends({
                state_code: stateCode,
            }),
        ]);

        const placeholders = await getPlaceholders(
            recommends.map(({ id }) => id),
            'activity'
        );

        recommends.forEach((i) => {
            i.placeholder = placeholders[i.id] || '';
        });

        const destinations = Object.values(state.items);

        if (!destinations.length) {
            return {
                notFound: true,
            };
        }

        const images = mainImage
            ? [mainImage, mainImage.replace('.jpg', '.webp')]
            : [
                  `${URL.image}/destination/${destinations[0]?.id}/${destinations[0]?.slug}.jpg`,
                  `${URL.image}/destination/${destinations[0]?.id}/${destinations[0]?.slug}.webp`,
              ];

        const breadcrumbs = [
            {
                url: '/destination/',
                title: 'Destination',
            },
            {
                url: `/destination/${stateCode}`,
                title: state.name,
                unlink: true,
            },
        ];

        const meta = {
            title: `${state.name} Things To Do`,
            description: `Discover the best things to do in ${state.name}, from outdoor adventures to family attractions. Explore scenic tours, water activities, and hidden local gems today.`,
            canonical: buildRootUrl(`destination/${stateCode}/`),
        };

        return {
            revalidate: Number(process.env.CACHE_ENV),
            props: {
                meta,
                state,
                blogs,
                landmarks,
                breadcrumbs,
                images,
                recommendedTrips: recommends,
                recommendedIds: recommends.map(({ id }) => id),
            },
        };
    } catch (e) {
        throw new Error((e as Error).message);
    }
};

export default StatePage;
